function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsxRuntime classic */

/** @jsx jsx */
import { memo, useRef, useEffect, useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import { success5, success7, warning5, warning7, danger5, danger7, info5, info7, brand, blue13, dark1, dark4, dark6, dark12 } from '../styles/colors';
import * as Icons from './basic';
import { capitalize } from '../utils';
import { useTheme } from '../theme';
const {
  colors
} = useTheme();

const getColor = name => {
  switch (capitalize(name)) {
    case 'Approval':
      return {
        color: success5,
        pressed: success7
      };

    case 'Delete':
      return {
        color: warning5,
        pressed: warning7
      };

    case 'Cancel':
    case 'Close':
      return {
        color: danger5,
        pressed: danger7
      };

    case 'Edit':
    case 'View':
      return {
        color: info5,
        pressed: info7
      };

    default:
      return {
        color: brand,
        pressed: '#697CCE'
      };
  }
};

const Icon = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  type,
  name,
  className,
  color,
  width,
  height,
  viewBox,
  disabled,
  onClick,
  ...props
}, ref) => {
  if (!ref) {
    ref = useRef(null);
  }

  const dgnIcon = css`
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: max-content;
        height: max-content;
        min-width: ${width ? isNaN(width) ? width : width + 'px' : '24px'};
        min-height: ${width ? isNaN(height) ? height : height + 'px' : '24px'};
        padding: ${viewBox ? type === 'filled' ? 0 : 4 : Math.floor((width || 24) / 8)}px;
        margin: ${type === 'filled' ? 4 : 0}px;
        color: ${type === 'filled' ? 'white' : colors[color] || color};
        background-color: ${type === 'filled' ? brand : 'transparent'};
        border-radius: 50%;
        outline: none;
        box-sizing: border-box;
        cursor: ${onClick ? 'pointer' : 'default'};
        user-select: none;
        &:hover {
            color: ${getColor(name).color};
            background-color: ${type === 'filled' ? blue13 : 'transparent'};
        }
        &:focus {
            color: ${type === 'filled' ? 'white' : getColor(name).pressed};
            background-color: ${type === 'filled' ? getColor(name).pressed : 'transparent'};
        }
        &:active {
            color: ${getColor(name).color};
            background-color: ${dark1};
        }
        &.disabled {
            color: ${dark4} !important;
            background-color: ${dark12} !important;
            pointer-events: none;
        }
    `;
  useEffect(() => {
    if (disabled && ref.current) {
      ref.current.classList.add('disabled');
    }

    return () => {
      if (disabled && ref.current) {
        ref.current.classList.remove('disabled');
      }
    };
  }, [disabled]);
  const IconEffect = useMemo(() => {
    if (!name) return null;
    const IconComp = Icons[capitalize(name)];
    if (!IconComp) return null;
    return jsx("span", _extends({
      className: 'DGN-UI-Icon ' + className,
      css: dgnIcon,
      tabIndex: -1,
      ref: ref,
      onClick: onClick
    }, props), jsx(IconComp, {
      width: width,
      height: height,
      color: 'currentColor',
      viewBox: viewBox
    }));
  }, [name, type, color, width, height, onClick]);
  return IconEffect;
}));
Icon.defaultProps = {
  type: 'default',
  className: '',
  color: dark6,
  disabled: false,
  viewBox: false
};
Icon.propTypes = {
  /** used to fill/non-fill background for icon */
  type: PropTypes.oneOf(['default', 'filled']),

  /** color of icon<br />
   * default: #7F828E
   */
  color: PropTypes.string,

  /** className of icon in icons store */
  className: PropTypes.string,

  /** name of icon in icons store */
  name: PropTypes.string.isRequired,

  /** the width of icon */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** the height of icon */
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** have view box if true */
  viewBox: PropTypes.bool,

  /** prevent all event if true */
  disabled: PropTypes.bool,

  /** function for click event on icon */
  onClick: PropTypes.func
};
export default Icon;